import { z } from 'zod';
import { canonicalJson, sha256Hex } from './canonicalJson';
import { AUTHORING_PROFILE, authoringTimeSchema, recordingSpanSchema } from './authoringPrimitives';
import { type InteractionTag, type ReferenceFrameAsset, type ReferenceFrameCatalog, interactionTagSchema,
  referenceFrameCatalogSchema } from './interactionTags';
import { validateInteractionReferenceMetadata, validateInteractionReferences } from './interactionReferenceValidation';
import { RAW_PACKAGE_FILES, type RawFrameRow, type RawPackageFileRef, type RawPackageFileSource, rawFrameRowSchema,
  rawPackageManifestSchema } from './rawPackage';
import { parseJsonLines, type RawInputEvent, validateRawInputStream } from './rawInputEvent';
import { MACRO_PACKAGE_LIMITS } from './limits';

export const RAW_AUTHORING_PACKAGE_FILES = { ...RAW_PACKAGE_FILES, interactions: 'interactions.jsonl',
  referenceFrames: 'reference-frames/index.json' } as const;
const previous = rawPackageManifestSchema.innerType();
const fileReference = previous.shape.policy;
export const rawAuthoringPackageManifestSchema = previous.extend({
  formatVersion: z.union([z.literal(3), z.literal(4)], {
    errorMap: () => ({ message: 'legacy_macro_requires_rerecording: raw v3 or v4 is required' }) }),
  authoringProfile: z.literal(AUTHORING_PROFILE), recordingSpan: recordingSpanSchema,
  capture: previous.shape.capture.extend({ frameIntervalUs: authoringTimeSchema.refine(value => value > 0),
    jitterBoundUs: authoringTimeSchema }),
  interactionTags: fileReference.extend({ file: z.literal(RAW_AUTHORING_PACKAGE_FILES.interactions),
    tagCount: authoringTimeSchema.max(MACRO_PACKAGE_LIMITS.rawMaxInputEvents) }),
  referenceFrames: fileReference.extend({ file: z.literal(RAW_AUTHORING_PACKAGE_FILES.referenceFrames),
    bytes: authoringTimeSchema.max(MACRO_PACKAGE_LIMITS.rawReferenceCatalogMaxJsonBytes),
    frameCount: authoringTimeSchema.max(MACRO_PACKAGE_LIMITS.rawMaxFrames) }),
  screenStates: z.tuple([]), regions: previous.shape.regions.extend({ regionCount: z.literal(0) }),
}).strict().superRefine((manifest, context) => {
  const fail = (message: string): void => context.addIssue({ code: z.ZodIssueCode.custom, message });
  if (manifest.recordingSpan.startUs < 200_000 + manifest.capture.frameIntervalUs
    || manifest.recordingSpan.stopUs > manifest.durationUs) fail('Recording span requires native pre-roll and a sealed stop');
  if (manifest.integrity.writtenInputEvents !== manifest.inputs.eventCount
    || manifest.integrity.encodedVideoFrames !== manifest.video.frameCount
    || manifest.integrity.indexedVideoFrames !== manifest.frames.frameCount
    || (manifest.capture.source === 'desktop' && manifest.integrity.admittedEditorPackets !== 0)) fail('recording_integrity_failed: manifest counters disagree');
  if (manifest.interactionTags.tagCount > manifest.inputs.eventCount) fail('Interaction tags cannot outnumber admitted inputs');
  if (manifest.referenceFrames.frameCount > manifest.frames.frameCount) fail('Reference assets cannot outnumber captured frames');
  if (manifest.formatVersion === 4 && manifest.referenceFrames.frameCount !== 0) fail('Raw v4 reference catalog must be empty');
  if (manifest.inputs.eventCount < 2 || manifest.inputs.eventCount > MACRO_PACKAGE_LIMITS.rawMaxInputEvents
    || manifest.frames.frameCount < 1 || manifest.frames.frameCount > MACRO_PACKAGE_LIMITS.rawMaxFrames) {
    fail('Native streams must retain terminal markers and fit the bounded raw package limits');
  }
  for (const key of ['video', 'frames', 'inputs', 'regions', 'policy'] as const) {
    if (manifest[key].file !== RAW_PACKAGE_FILES[key]) fail(`Invalid canonical ${key} file`);
  }
  const rect = manifest.capture.rect;
  if (rect.right - rect.left !== manifest.capture.width || rect.bottom - rect.top !== manifest.capture.height) {
    fail('Capture dimensions must describe the pinned native source rectangle');
  }
  if (manifest.video.width !== manifest.capture.width || manifest.video.height !== manifest.capture.height
    || manifest.video.durationUs > manifest.durationUs) fail('Encoded geometry and clock horizon must agree with capture');
  for (const key of ['video', 'frames', 'inputs', 'regions', 'policy', 'interactionTags', 'referenceFrames'] as const) {
    if (!Number.isSafeInteger(manifest[key].bytes)) fail('Pinned byte counts must be safe integers');
  }
});
export type RawAuthoringPackageManifest = z.infer<typeof rawAuthoringPackageManifestSchema>;

export interface RawAuthoringReferenceClosure {
  manifest: RawAuthoringPackageManifest; events: readonly RawInputEvent[]; frames: readonly RawFrameRow[];
  tags: readonly InteractionTag[]; catalog: ReferenceFrameCatalog;
}
/** Reference closure only. The pipeline separately verifies all pinned files, including video, by streaming. */
export async function validateRawAuthoringReferences(source: RawPackageFileSource,
  closure: RawAuthoringReferenceClosure): Promise<readonly string[]> {
  const parsed = rawAuthoringPackageManifestSchema.safeParse(closure.manifest);
  if (!parsed.success) return parsed.error.issues.map(issue => issue.message);
  const catalog = referenceFrameCatalogSchema.safeParse(closure.catalog);
  const tags = z.array(interactionTagSchema).safeParse(closure.tags);
  const frames = z.array(rawFrameRowSchema).safeParse(closure.frames);
  const inputs = validateRawInputStream(closure.events);
  if (!catalog.success || !tags.success || !frames.success || !inputs.ok) {
    return ['recording_reference_metadata_invalid']; // Do not read unvalidated asset paths.
  }
  const manifest = parsed.data;
  if (manifest.formatVersion === 4 && closure.catalog.frames.length !== 0) return ['raw_v4_reference_catalog_not_empty'];
  const validate = manifest.formatVersion === 4 ? validateInteractionReferenceMetadata : validateInteractionReferences;
  const issues = [...validate({ ...closure, packageId: manifest.packageId, span: manifest.recordingSpan,
    frameIntervalUs: manifest.capture.frameIntervalUs, jitterBoundUs: manifest.capture.jitterBoundUs,
    width: manifest.capture.width, height: manifest.capture.height })];
  if (closure.events.length !== manifest.inputs.eventCount || closure.frames.length !== manifest.frames.frameCount
    || closure.tags.length !== manifest.interactionTags.tagCount || closure.catalog.frames.length !== manifest.referenceFrames.frameCount) {
    issues.push('recording_metadata_count_mismatch');
  }
  await verifyMetadataFile(source, manifest.interactionTags, closure.tags, true, issues);
  await verifyMetadataFile(source, manifest.referenceFrames, closure.catalog, false, issues);
  await verifyMetadataFile(source, manifest.regions, { regions: [] }, false, issues);
  for (const asset of closure.catalog.frames) await verifyReferenceAsset(source, asset, issues);
  return issues;
}

async function verifyMetadataFile(source: RawPackageFileSource, pin: RawPackageFileRef,
  expected: unknown, jsonLines: boolean, issues: string[]): Promise<void> {
  try {
    const bytes = await source.fileBytes(pin.file);
    if (bytes.length !== pin.bytes || await sha256Hex(bytes) !== pin.sha256) issues.push(`${pin.file}: pin mismatch`);
    const text = new TextDecoder().decode(bytes);
    const value: unknown = jsonLines ? parseJsonLines(text) : JSON.parse(text);
    if (canonicalJson(value) !== canonicalJson(expected)) issues.push(`${pin.file}: metadata mismatch`);
  } catch { issues.push(`${pin.file}: missing or invalid`); }
}

async function verifyReferenceAsset(source: RawPackageFileSource, asset: ReferenceFrameAsset, issues: string[]): Promise<void> {
  let bytes: Uint8Array;
  try { bytes = await source.fileBytes(asset.file); }
  catch { issues.push(`${asset.file}: missing`); return; }
  if (bytes.byteLength !== asset.bytes || await sha256Hex(bytes) !== asset.sha256) issues.push(`${asset.file}: pin mismatch`);
  if (!matchesPngHeader(bytes, asset)) issues.push(`${asset.file}: native image metadata mismatch`);
}

/** Header/precision check is not a decoder. Calibration must additionally decode and validate the PNG. */
function matchesPngHeader(bytes: Uint8Array, asset: ReferenceFrameAsset): boolean {
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  if (bytes.length < 33 || signature.some((byte, index) => bytes[index] !== byte)) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return view.getUint32(8) === 13 && view.getUint32(12) === 0x49484452
    && view.getUint32(16) === asset.width && view.getUint32(20) === asset.height && bytes[24] === asset.bitDepth;
}
